/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Saturday, August 27th 2022, 6:41:07 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import CheckIcon from "@mui/icons-material/Check";
import { TERMS_OF_SERVICE_URL } from "config";
import { getPurchaseFlowStrategyHook } from "saas-subscription/strategy";
import { SubscriptionPlan } from "saas-subscription/schemas";
import { OSActionButton, OSAlert } from "ui-widgets";

interface PurchaseFormProps {
  onConfirmPurchase: () => void;
  subscriptionPlan: SubscriptionPlan;
  purchaseFormTitle: React.ReactNode;
  purchaseFormBody: React.ReactNode;
  strategy: ReturnType<ReturnType<typeof getPurchaseFlowStrategyHook>>;
}

export const PurchaseForm = ({
  subscriptionPlan,
  onConfirmPurchase,
  purchaseFormTitle,
  purchaseFormBody,
  strategy,
}: PurchaseFormProps) => {
  const { packageColor, packageGradient, packageRGBTriplets } = strategy.getWidgets(subscriptionPlan);

  const [acceptTermsOfService, setAcceptTermsOfService] = React.useState(false);

  const [acceptTermsOfServiceError, setAcceptTermsOfServiceError] = React.useState<string>();

  const confirmPurchase = () => {
    if (acceptTermsOfService === true) {
      onConfirmPurchase();
      return;
    }

    setAcceptTermsOfServiceError("Please agree to the Terms of Service in order to continue");
  };

  React.useEffect(() => {
    if (acceptTermsOfService) {
      setAcceptTermsOfServiceError(null);
      return;
    }
  }, [acceptTermsOfService]);

  return (
    <Grid
      container
      columnSpacing={4}
      sx={{
        backgroundImage: `linear-gradient(-60deg, rgb(${packageRGBTriplets}, 15%) 20%, #fff 60%);`,
        padding: 5,
      }}
    >
      <Grid item md={4}>
        <Grid container>
          <Box component={Grid} item md={12} display={{ sm: "none", md: "block" }}>
            <Paper
              sx={{
                padding: 2,
                background: packageGradient,
              }}
              elevation={10}
            >
              <Typography variant="h6">
                <p>{subscriptionPlan.name} comes with the following features.</p>
              </Typography>
              <p>
                {subscriptionPlan.features.map((feature) => (
                  <p key={feature}>
                    <CheckIcon fontSize="small" /> {feature}
                  </p>
                ))}
              </p>
            </Paper>
          </Box>
        </Grid>
      </Grid>
      <Grid item md={8}>
        <Grid container spacing={2}>
          <Grid item md={12}>
            {purchaseFormTitle}
          </Grid>
          <Grid item md={12} sx={{ paddingBottom: 0 }}>
            {purchaseFormBody}
          </Grid>
          <Grid item md={12} sx={{ paddingBottom: 0 }}>
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={(event) => {
                      setAcceptTermsOfService(event.target.checked);
                    }}
                    checked={acceptTermsOfService}
                  />
                }
                label={
                  <>
                    I agree to the{" "}
                    <a href={TERMS_OF_SERVICE_URL} target="_blank" rel="noreferrer">
                      Terms of Service
                    </a>
                  </>
                }
              />
              {acceptTermsOfServiceError && (
                <p>
                  <OSAlert severity="error" variant="filled">
                    {acceptTermsOfServiceError}
                  </OSAlert>
                </p>
              )}
            </>
          </Grid>
          <Grid item md={12}>
            <OSActionButton
              sx={{ background: packageColor }}
              onClick={confirmPurchase}
              fullWidth
              size="large"
              variant="contained"
            >
              Continue
            </OSActionButton>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
